import Api from "../../utils/Api";
import React, { useState } from "react";
import FirewallTable from "./FirewallTable";
import FirewallPolicyLayout from "./FirewallPolicyLayout";
import { Box, useToast, useTranslation } from "@familyzone/component-library";
import PageWithHeader from "../../components/templates/PageWithHeader";
import { FirewallRule } from "./FirewallRule";
import FirewallRuleModal from "./FirewallRuleModal";
import validateCriteria from "../../modules/criteria/validateCriteria";
import { v4 as uuid } from "uuid";

const Firewallnew: React.FC = () => {
  const { t } = useTranslation();

  const breadcrumbs = [
    { title: t("Edge Networking"), url: "/edgewize/status", isActive: false },
    { title: t("Firewall"), isActive: true },
  ];

  const title = t("Firewall");

  const blankRule: FirewallRule = {
    action: 0,
    comment: "",
    destination_criteria: [],
    enabled: false,
    id: "0",
    log: false,
    source_criteria: [],
  };

  const [rules, setRules] = useState<FirewallRule[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [editingRule, setEditingRule] = useState<FirewallRule>(blankRule);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [create, setCreate] = useState<boolean>(false);

  const { errorToast } = useToast();

  const validateRule = (rule: FirewallRule): boolean => {
    const srcCriteriaValidationResult = validateCriteria(rule.source_criteria);
    const [srcHasErrors, sourceCriterias] = srcCriteriaValidationResult;

    if (srcHasErrors) {
      sourceCriterias.forEach((sourceCriteria) => {
        if (sourceCriteria.errorMessage) {
          errorToast({ title: sourceCriteria.errorMessage, isClosable: true });
        }
      });
      return false;
    }

    const dstCriteriaValidationResult = validateCriteria(rule.destination_criteria);
    const [dstHasErrors, destinationCriterias] = dstCriteriaValidationResult;

    if (dstHasErrors) {
      destinationCriterias.forEach((destinationCriteria) => {
        if (destinationCriteria.errorMessage) {
          errorToast({ title: destinationCriteria.errorMessage, isClosable: true });
        }
      });
      return false;
    }

    return true;
  };

  const handleLoad = () => {
    setLoading(true);
    Api.get("/edgewize/ajax/firewall/filtering", function (result: { data: FirewallRule[] }) {
      setRules(result["data"]);
      setLoading(false);
    });
  };

  React.useEffect(() => {
    handleLoad();
  }, []);

  const handleSortRules = async (newRules: FirewallRule[]) => {
    const reorderedRulesIds = newRules.map((rule: FirewallRule) => rule.id);
    const stringIds = reorderedRulesIds.join(",");
    const previousOrder = rules;

    setRules(newRules);
    setLoading(true);
    try {
      await Api.postAsync("/edgewize/device/firewall/filtering/reorder", { order: stringIds });
    } catch (err) {
      errorToast({ title: "Error occurred while moving policy", isClosable: true });
      setRules(previousOrder);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRule = async (newRule: FirewallRule) => {
    newRule.enabled = !newRule.enabled;

    setLoading(true);
    try {
      await Api.postAsync("/edgewize/ajax/firewall/filtering", newRule);
    } catch (err) {
      errorToast({ title: "Error occurred while enabling policy", isClosable: true });
      newRule.enabled = !newRule.enabled;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRule = async (deleteRule: FirewallRule) => {
    const newRules: FirewallRule[] = [];
    rules.forEach((rule: FirewallRule) => {
      if (rule.id !== deleteRule.id) {
        newRules.push(rule);
      }
    });

    setLoading(true);
    try {
      await Api.deleteAsync("/edgewize/ajax/firewall/filtering", { id: deleteRule.id });
    } catch (err) {
      errorToast({ title: "Error occurred while deleting policy", isClosable: true });
      return;
    } finally {
      setLoading(false);
    }

    setRules(newRules);
  };

  const handleSaveRule = async (newRule?: FirewallRule) => {
    if (!newRule) {
      return;
    }
    if (!validateRule(newRule)) {
      return;
    }
    const newRules: FirewallRule[] = [];
    let edited = false;
    rules.forEach((rule: FirewallRule) => {
      if (rule.id === newRule.id) {
        newRules.push(newRule);
        edited = true;
      } else {
        newRules.push(rule);
      }
    });
    if (!edited) {
      newRule["id"] = uuid();
      newRules.push(newRule);
    }
    setLoading(true);
    try {
      await Api.postAsync("/edgewize/ajax/firewall/filtering", newRule);
    } catch (err) {
      errorToast({ title: "Error occurred while saving policy", isClosable: true });
      return;
    } finally {
      setLoading(false);
    }
    setRules(newRules);
    setModalIsOpen(false);
  };

  const handleEditRule = (rule: FirewallRule) => {
    setCreate(false);
    setEditingRule(rule);
    setModalIsOpen(true);
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
  };

  const handleClickCreatePolicy = () => {
    setCreate(true);
    setEditingRule(blankRule);
    setModalIsOpen(true);
  };

  return (
    <PageWithHeader breadcrumbs={breadcrumbs} title={title}>
      <Box width="100%" mt="sp16">
        <FirewallRuleModal
          existingRule={editingRule}
          handleSubmit={(newRule?: FirewallRule) => void handleSaveRule(newRule)}
          isOpen={modalIsOpen}
          closeModal={handleCloseModal}
          create={create}
          busy={loading}
        />
        <FirewallPolicyLayout
          loading={loading && rules.length === 0}
          disabled={loading && rules.length !== 0}
          showObjectLists
          onClickCreatePolicy={() => void handleClickCreatePolicy()}
        >
          <FirewallTable
            disabled={loading && rules.length !== 0}
            loading={loading && rules.length === 0}
            rules={rules}
            onSortRules={(rules: FirewallRule[]) => void handleSortRules(rules)}
            onToggleRule={(rule: FirewallRule) => void handleToggleRule(rule)}
            onDeleteRule={(rule: FirewallRule) => void handleDeleteRule(rule)}
            onEditRule={(rule: FirewallRule) => void handleEditRule(rule)}
          />
        </FirewallPolicyLayout>
      </Box>
    </PageWithHeader>
  );
};

export default Firewallnew;
